import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type ResponseType,
} from 'axios';
import { toast } from 'sonner';

import { clearAccessTokens, getAccessToken, setAccessToken } from '@/api/tokens.helper.ts';
import type { ApiException } from '@/common/error.ts';
import { ROUTES } from '@/router/routes.ts';

const LONG_PULL_TIMEOUT = 10 * 60 * 1000;
const NORMAL_REQUEST_TIMEOUT = 15000;

export enum API {
  PEOPLE = 'people',
  VESSEL = 'vessel',
}

const APIS: Record<API, AxiosInstance> = {
  [API.PEOPLE]: axios.create({
    baseURL: `${import.meta.env.VITE_APP_PEOPLE_URL}`,
    headers: { 'Content-Type': 'application/json' },
    paramsSerializer: { indexes: null },
  }),
  // Subscriptions live on the People service, but the vessels they belong to
  // (name, image) come from the vessel service.
  [API.VESSEL]: axios.create({
    baseURL: `${import.meta.env.VITE_APP_VESSEL_URL}`,
    headers: { 'Content-Type': 'application/json' },
    paramsSerializer: { indexes: null },
  }),
};

const setTokensToAPIs = (accessToken?: string) => {
  Object.values(APIS).forEach((api) => {
    if (!api) return;
    if (accessToken) {
      api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  });
};

setTokensToAPIs(getAccessToken() || undefined);

const handleUnauthenticated = (): void => {
  clearAccessTokens();
  setTokensToAPIs(undefined);
  window.location.assign(ROUTES.UNAUTHORIZED);
};

/**
 * A blob request answers its errors as a blob too — the same JSON body, merely unparsed.
 * Read it back so a refused file fetch throws the `ApiException` every other call throws, and
 * the JWT redirect and the error helpers keep working for files as well.
 */
const readErrorBody = async (data: unknown): Promise<unknown> => {
  if (!(data instanceof Blob)) return data;
  try {
    return JSON.parse(await data.text());
  } catch {
    return data;
  }
};

const isNetworkError = (error: any): boolean => {
  return (
    error?.code === 'ERR_NETWORK' ||
    error?.message?.includes('Network Error') ||
    error?.message?.includes('Failed to fetch')
  );
};

const request = async <T, D>(
  apiKey: API,
  url: string,
  method: string,
  data?: D,
  isLongPull = false,
  params?: Record<string, any>,
  serviceId?: string,
  responseType?: ResponseType,
): Promise<AxiosResponse<T>> => {
  const requestDetails: AxiosRequestConfig<D> = {
    method,
    url,
    timeout: isLongPull ? LONG_PULL_TIMEOUT : NORMAL_REQUEST_TIMEOUT,
    responseType,
  };

  // Most endpoints are service-scoped and reject the request without this:
  // {"domain":"PEOPLE_HEADER","type":"FRONTEND_INPUTS","dev_message":"X-Service-Id header not found"}.
  // Passed per call rather than held globally, so a screen scoped to one vessel can
  // name that vessel instead of whatever the session last happened to use.
  if (serviceId) {
    requestDetails.headers = { ...requestDetails.headers, 'X-Service-Id': serviceId };
  }

  if (data) {
    requestDetails.data = data;
  }

  if (params) {
    requestDetails.params = params;
  }

  if (data instanceof FormData) {
    requestDetails.headers = { ...requestDetails.headers, 'Content-Type': 'multipart/form-data' };
  }

  const api = APIS[apiKey];
  try {
    return await api.request<T>(requestDetails);
  } catch (error) {
    if (axios.isCancel(error)) {
      return new Promise<AxiosResponse<T>>(() => {});
    }
    if (axios.isAxiosError(error)) {
      if (isNetworkError(error)) {
        toast.error("Can't load all data. Please check your internet connection.", {
          id: 'offline-error',
        });
      }

      if (error.response) {
        const exception = (await readErrorBody(error.response.data)) as ApiException;
        if (exception.domain === 'JWT' || exception.domain === 'AUTH_HEADER') {
          handleUnauthenticated();
        }
        throw exception;
      }
      throw error;
    }
    throw error;
  }
};

export const login = (accessToken: string) => {
  setAccessToken(accessToken);
  setTokensToAPIs(accessToken);
};

export const getRequest = async <T>(
  apiKey: API,
  url: string,
  isLongPull = false,
  params?: Record<string, any>,
  serviceId?: string,
) => request<T, undefined>(apiKey, url, 'GET', undefined, isLongPull, params, serviceId);

/**
 * A file behind a private link — a vessel image, a person's avatar. The link is absolute,
 * so it is passed whole rather than as a path: axios leaves the base URL off an absolute
 * URL, and the client still adds the token (and the service id, where one is needed).
 */
export const getRequestBlob = async (
  apiKey: API,
  url: string,
  serviceId?: string,
): Promise<Blob> => {
  const { data } = await request<Blob, undefined>(
    apiKey,
    url,
    'GET',
    undefined,
    false,
    undefined,
    serviceId,
    'blob',
  );
  return data;
};

export const postRequest = async <T, D>(
  apiKey: API,
  url: string,
  data: D,
  isLongPull = false,
  serviceId?: string,
) => request<T, D>(apiKey, url, 'POST', data, isLongPull, undefined, serviceId);

export const putRequest = async <T, D>(
  apiKey: API,
  url: string,
  data: D,
  isLongPull = false,
  serviceId?: string,
) => request<T, D>(apiKey, url, 'PUT', data, isLongPull, undefined, serviceId);

export const patchRequest = async <T, D>(
  apiKey: API,
  url: string,
  data: D,
  isLongPull = false,
  serviceId?: string,
) => request<T, D>(apiKey, url, 'PATCH', data, isLongPull, undefined, serviceId);

export const deleteRequest = async <T, D = undefined>(
  apiKey: API,
  url: string,
  data?: D,
  isLongPull = false,
  params?: Record<string, any>,
  serviceId?: string,
) => request<T, D | undefined>(apiKey, url, 'DELETE', data, isLongPull, params, serviceId);
